import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import NavBarScnd from "../pages/NavBar/NavBarScnd";
import "./BsGlasses.css";

const MODEL_URL = "/base2.glb";
const MODEL_SCALE = 6;
const MODEL_ROTATION = { x: 0, y: 4, z: 0 };
const MODEL_POSITION = { x: -0.4, y: 1, z: -3 };
const HEAD_OCCLUDER_BASE = new THREE.Vector3(2.35, 3.6, 2.35);
const POS_Z_SCALE = 0.02;
const AUTO_CENTER_X = true;
const CENTER_STRENGTH = 3.0;
const OCCLUSION_ENABLED = true;
const OCCLUDER_SIZE = 4.06;
const OCCLUDER_OFFSET_X = 0;
const OCCLUDER_OFFSET_Z = -11.12;
const OCCLUDER_YAW_SHIFT = 3.6;
const SHOW_OCCLUDER_DEBUG = false;

export default function GlassesV3() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const faceAnchorRef = useRef(null);
  const glassesRef = useRef(null);
  const headOccluderRef = useRef(null);
  const headOccluderDepthRef = useRef(null);
  const headOccluderDebugRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const processingRef = useRef(false);
  const lastVideoSizeRef = useRef({ width: 0, height: 0 });
  const rafRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const streamRef = useRef(null);
  const tempMatrixRef = useRef(new THREE.Matrix4());
  const tempPosRef = useRef(new THREE.Vector3());
  const tempQuatRef = useRef(new THREE.Quaternion());
  const tempScaleRef = useRef(new THREE.Vector3());
  const targetFacePosRef = useRef(new THREE.Vector3());
  const occluderBaseQuatRef = useRef(
    new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, 0)),
  );
  const tempForwardRef = useRef(new THREE.Vector3(0, 0, 1));
  const occluderBaseXRef = useRef(0);
  const occluderBaseZRef = useRef(OCCLUDER_OFFSET_Z);
  const autoCenterOffsetRef = useRef(0);
  const statusRef = useRef("initializing");
  const faceDetectedRef = useRef(false);
  const posZRef = useRef(MODEL_POSITION.z);
  const [, setStatus] = useState("initializing");
  const [, setFaceDetected] = useState(false);
  const [, setModelLoaded] = useState(false);
  const [modelError, setModelError] = useState("");
  const [sendError, setSendError] = useState("");
  const [, setVideoInfo] = useState({ width: 0, height: 0 });

  const setStatusSafe = (next) => {
    if (statusRef.current !== next) {
      statusRef.current = next;
      setStatus(next);
    }
  };

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: false,
        });
      } catch (err) {
        setStatusSafe("camera-error");
        setModelError(err?.message || "Camera permission error");
        return;
      }

      if (!mounted || !videoRef.current) return;
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      initThree();
      await loadGlasses();
      await initFaceLandmarker();
      animate();
    };

    start();

    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => t.stop());
      if (faceLandmarkerRef.current) faceLandmarkerRef.current.close();
      if (rendererRef.current) rendererRef.current.dispose();
    };
  }, []);

  useEffect(() => {
    const occluder = headOccluderRef.current;
    const occluderDepth = headOccluderDepthRef.current;
    const occluderDebug = headOccluderDebugRef.current;
    if (!occluder) return;
    occluder.visible = OCCLUSION_ENABLED;
    if (occluderDepth) occluderDepth.visible = OCCLUSION_ENABLED;
    if (occluderDebug) occluderDebug.visible = SHOW_OCCLUDER_DEBUG;
    occluder.quaternion.copy(occluderBaseQuatRef.current);
    occluder.scale.set(
      HEAD_OCCLUDER_BASE.x * OCCLUDER_SIZE,
      HEAD_OCCLUDER_BASE.y * OCCLUDER_SIZE,
      HEAD_OCCLUDER_BASE.z * OCCLUDER_SIZE,
    );
    occluderBaseXRef.current = OCCLUDER_OFFSET_X;
    occluderBaseZRef.current = OCCLUDER_OFFSET_Z;
    occluder.position.set(OCCLUDER_OFFSET_X, 0, OCCLUDER_OFFSET_Z);
  }, []);

  const initThree = () => {
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 16 / 9, 0.01, 100);
    camera.position.set(0, 0, 1.5);

    const faceAnchor = new THREE.Group();
    faceAnchor.visible = false;
    scene.add(faceAnchor);

    const occluderGroup = new THREE.Group();
    const occluderGeo = new THREE.SphereGeometry(1, 32, 24);
    const occluderDepthMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      depthWrite: true,
      depthTest: true,
    });
    occluderDepthMat.colorWrite = false;
    const occluderDebugMat = new THREE.MeshBasicMaterial({
      color: 0x7fc8ff,
      depthWrite: false,
      depthTest: true,
      transparent: true,
      opacity: 0.35,
    });
    const depthMesh = new THREE.Mesh(occluderGeo, occluderDepthMat);
    const debugMesh = new THREE.Mesh(occluderGeo, occluderDebugMat);
    depthMesh.renderOrder = 0;
    debugMesh.renderOrder = 0;
    occluderGroup.visible = OCCLUSION_ENABLED;
    depthMesh.visible = OCCLUSION_ENABLED;
    debugMesh.visible = SHOW_OCCLUDER_DEBUG;
    occluderGroup.scale.set(
      HEAD_OCCLUDER_BASE.x * OCCLUDER_SIZE,
      HEAD_OCCLUDER_BASE.y * OCCLUDER_SIZE,
      HEAD_OCCLUDER_BASE.z * OCCLUDER_SIZE,
    );
    occluderGroup.position.set(OCCLUDER_OFFSET_X, 0, OCCLUDER_OFFSET_Z);
    occluderGroup.add(depthMesh, debugMesh);
    headOccluderRef.current = occluderGroup;
    headOccluderDepthRef.current = depthMesh;
    headOccluderDebugRef.current = debugMesh;
    faceAnchor.add(occluderGroup);

    scene.add(new THREE.AmbientLight(0xffffff, 1.8));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(0.3, 0.8, 1.5);
    scene.add(key);

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    faceAnchorRef.current = faceAnchor;
  };

  const loadGlasses = async () => {
    const loader = new GLTFLoader();
    setStatusSafe("loading-model");
    try {
      const gltf = await loader.loadAsync(MODEL_URL);
      const model = gltf.scene;
      model.scale.setScalar(MODEL_SCALE);
      model.rotation.set(
        THREE.MathUtils.degToRad(MODEL_ROTATION.x),
        THREE.MathUtils.degToRad(MODEL_ROTATION.y),
        THREE.MathUtils.degToRad(MODEL_ROTATION.z),
      );
      model.position.set(MODEL_POSITION.x, MODEL_POSITION.y, MODEL_POSITION.z);

      const lensMaterial = new THREE.ShaderMaterial({
        transparent: true,
        uniforms: {
          uTint: { value: new THREE.Color(0x88c8ff) },
          uOpacity: { value: 0.22 },
          uFresnelPower: { value: 3.0 },
        },
        vertexShader: `
        varying vec3 vNormal;
        varying vec3 vViewDir;
        void main() {
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vNormal = normalize(mat3(modelMatrix) * normal);
          vViewDir = normalize(cameraPosition - worldPos.xyz);
          gl_Position = projectionMatrix * viewMatrix * worldPos;
        }
      `,
        fragmentShader: `
        uniform vec3 uTint;
        uniform float uOpacity;
        uniform float uFresnelPower;
        varying vec3 vNormal;
        varying vec3 vViewDir;

        void main() {
          float fresnel = pow(1.0 - max(dot(normalize(vNormal), normalize(vViewDir)), 0.0), uFresnelPower);
          vec3 color = mix(uTint * 0.6, vec3(1.0), fresnel * 0.4);
          float alpha = uOpacity + fresnel * 0.18;
          gl_FragColor = vec4(color, alpha);
        }
      `,
      });

      model.traverse((child) => {
        if (!child.isMesh) return;
        child.frustumCulled = false;
        child.renderOrder = 1;

        // Example: put shader only on lens meshes by name
        if (child.name.toLowerCase().includes("lens")) {
          child.material = lensMaterial;
        } else if (child.material) {
          child.material.depthTest = true;
          child.material.depthWrite = true;
          child.material.transparent = true;
          child.material.opacity = 0.7;
        }
      });

      model.visible = false;
      glassesRef.current = model;
      faceAnchorRef.current.add(model);
      setModelLoaded(true);
      if (statusRef.current === "waiting-model") {
        setStatusSafe("ready");
      } else {
        setStatusSafe("ready");
      }
    } catch (err) {
      setModelLoaded(false);
      setStatusSafe("model-error");
      setModelError(err?.message || "Failed to load base.glb");
    }
  };

  const initFaceLandmarker = async () => {
    try {
      setStatusSafe("loading-landmarker");
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
      );
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        vision,
        {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: true,
        },
      );
      setStatusSafe("ready");
    } catch (err) {
      setStatusSafe("landmarker-error");
      setModelError(err?.message || "Failed to init FaceLandmarker");
    }
  };

  const syncRendererToVideo = () => {
    const video = videoRef.current;
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    if (!video || !renderer || !camera) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    const last = lastVideoSizeRef.current;
    if (last.width === width && last.height === height) return;

    lastVideoSizeRef.current = { width, height };
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    setVideoInfo({ width, height });
  };

  const updateFaceAnchorFromArray = (matrixArray) => {
    const faceAnchor = faceAnchorRef.current;
    if (!faceAnchor || !matrixArray || matrixArray.length < 16) return;

    const m = tempMatrixRef.current;
    m.fromArray(matrixArray);

    const pos = tempPosRef.current;
    const quat = tempQuatRef.current;
    const scl = tempScaleRef.current;
    m.decompose(pos, quat, scl);

    const forward = tempForwardRef.current.set(0, 0, 1).applyQuaternion(quat);
    const targetPos = targetFacePosRef.current
      .copy(pos)
      .addScaledVector(forward, posZRef.current * POS_Z_SCALE);

    faceAnchor.visible = true;
    faceAnchor.position.lerp(targetPos, 0.45);
    faceAnchor.quaternion.slerp(quat, 0.35);
    faceAnchor.scale.lerp(scl, 0.35);

    const occluder = headOccluderRef.current;
    if (occluder) {
      occluder.quaternion.copy(occluderBaseQuatRef.current);
      const shift = forward.x * OCCLUDER_YAW_SHIFT;
      occluder.position.x = occluderBaseXRef.current + shift;
      occluder.position.z = occluderBaseZRef.current;
    }
  };

  const handleLandmarkerResults = (matrixArray, landmarks) => {
    const glasses = glassesRef.current;
    const faceAnchor = faceAnchorRef.current;
    const occluder = headOccluderRef.current;

    if (!matrixArray || matrixArray.length < 16) {
      if (faceDetectedRef.current) {
        faceDetectedRef.current = false;
        setFaceDetected(false);
      }
      if (
        statusRef.current === "face-detected" ||
        statusRef.current === "ready"
      ) {
        setStatusSafe("no-face");
      }
      if (faceAnchor) faceAnchor.visible = false;
      if (glasses) glasses.visible = false;
      if (occluder) occluder.visible = false;
      return;
    }

    if (!faceDetectedRef.current) {
      faceDetectedRef.current = true;
      setFaceDetected(true);
    }
    setStatusSafe("face-detected");
    updateFaceAnchorFromArray(matrixArray);

    if (AUTO_CENTER_X && landmarks && landmarks.length > 263) {
      const left = landmarks[33];
      const right = landmarks[263];
      const centerX = (left.x + right.x) / 2;
      const delta = centerX - 0.5;
      autoCenterOffsetRef.current = delta * CENTER_STRENGTH;
    } else {
      autoCenterOffsetRef.current = 0;
    }

    if (glasses) {
      glasses.position.x = MODEL_POSITION.x + autoCenterOffsetRef.current;
      glasses.position.y = MODEL_POSITION.y;
      glasses.position.z = MODEL_POSITION.z;
      glasses.scale.setScalar(MODEL_SCALE);
    }

    if (glasses) glasses.visible = true;
    if (occluder) occluder.visible = OCCLUSION_ENABLED;
    if (sendError) setSendError("");
  };

  const animate = () => {
    const video = videoRef.current;
    const faceLandmarker = faceLandmarkerRef.current;
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    syncRendererToVideo();

    const hasVideoFrame =
      video && video.videoWidth > 0 && video.videoHeight > 0;
    if (
      hasVideoFrame &&
      faceLandmarker &&
      !processingRef.current &&
      video.currentTime !== lastVideoTimeRef.current
    ) {
      lastVideoTimeRef.current = video.currentTime;
      processingRef.current = true;
      try {
        const results = faceLandmarker.detectForVideo(video, performance.now());
        const matrixArray =
          results.facialTransformationMatrixes?.[0]?.data || null;
        handleLandmarkerResults(matrixArray, results.faceLandmarks?.[0]);
      } catch (err) {
        setSendError(err?.message || "FaceLandmarker failed");
        setStatusSafe("send-error");
      } finally {
        processingRef.current = false;
      }
    }

    if (renderer && scene && camera) {
      renderer.clear();
      renderer.render(scene, camera);
    }

    rafRef.current = requestAnimationFrame(animate);
  };

  return (
    <div className="bsglasses-shell">
      <NavBarScnd
        isScrolling={false}
        isTitle={true}
        title="Try On"
        titleColor="#073F50"
        navColor="#073F50"
        isBGColor={true}
        bgColortxt="#fff"
      />
      <main className="bsglasses-page">
        <section className="bsglasses-stage" aria-label="Glasses try on">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="bsglasses-video"
          />
          <canvas ref={canvasRef} className="bsglasses-canvas" />
          {(sendError || modelError) && (
            <div className="bsglasses-message" role="status">
              {sendError && <div>{sendError}</div>}
              {modelError && <div>{modelError}</div>}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
